import React, { useEffect, useState, useRef } from 'react';
import store from 'redux-store/store';
import { FormFeedback, Row, Col } from 'reactstrap';
import auth from 'helpers/auth';
import moment from 'moment';
import Select from 'react-select';
import queryString from 'query-string';
import { Link, useLocation } from 'react-router-dom';
import { API, BASE_URL, NO_FILTERED_ID } from 'CONST';
import DropdownDownloadBtn from 'components/shared/DropdownDownloadBtn';
import { roundInt } from 'helpers/helpers';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import CreditPolicyDeudorLineasTable from './CreditPolicyDeudorLineasTable';
import isUserHasProfile from '../../../../helpers/profileValidator';
import { LINEA_CREDITO_WRITE } from '../../../../helpers/profilePermission';
import ModalLoadingMotor from '../../../shared/ModalLoadingMotor';
import InfoValueCard from '../../../shared/InfoValueCard';
import LoadingSpinner from '../../../shared/LoadingSpinner';
import Icon from "../../../UI/Icon";


const CreditPolicyDeudorLineas = ({ locationSearch, client }) => {
  const [polizas, setPolizas] = useState([]);
  const [selectedPoliza, setSelectedPoliza] = useState(null);
  const [selectedVigencia, setSelectedVigencia] = useState(null);
  const [allVigenciaOptions] = useState([{ index: -1, label: 'Todas', value: { id: -1, }}]);
  const [estadoLineaOptions, setEstadoLineaOptions] = useState([]);
  const [loadingGeneralInfo, setLoadingGeneralInfo] = useState(false);
  const [generalInfo, setGeneralInfo] = useState({});
  const [tiempoPromedioRespuesta, setTiempoPromedioRespuesta] = useState({});
  const history = useHistory();
  const location = useLocation();
  const params = queryString.parse(locationSearch);

  const state = store.getState();
  const { user } = state.login;

  const [processingModalOpen, setProcessingModalOpen] = useState(false); // Colocarlo en true para mostrar el modal siempre
  const [statusPending, setStatusPending] = useState(false);// Colocarlo en true para mostrar el modal siempre
  const [modalMessage, setModalMessage] = useState("La solicitud del motor se está procesando.");
  const [intervalId, setIntervalId] = useState(null);
  const [showButton, setShowButton] = useState(false);
  const [resultId, setResultId] = useState(null); // Nueva variable de estado para almacenar el ID
  const resultIdRef = useRef(resultId);
  const [lastEndosoCredito, setEndosoCredito] = useState([]);
  const [estadoFilter, setEstadoFilter] = useState(NO_FILTERED_ID); // Estado filter de la tabla usado por el dashboard

  const [tableLoading, setTableLoading] = useState(true);
  const [firstLoading, setFirstLoading] = useState(true);
  
  useEffect(() => {
    resultIdRef.current = resultId;
  }, [resultId]);

  const initialLoad = (credits) => {
    setPolizas(credits);
    const firstPolicyIndex = credits.length > 0 ? 0 : null;
    let policyIndex = -1;
    if (params.poliza_id) {
      const polizaId = parseInt(params.poliza_id, 10);
      policyIndex = credits.findIndex((policy) => policy.id === polizaId);
      setSelectedPoliza(policyIndex > -1 ? policyIndex : firstPolicyIndex);
      history.replace({
        pathname: '/asegurado/lineas-credito/',
        state: location.state,
      });
    } else if (credits.length === 1) {
      setSelectedPoliza(0);  // Selecciona automáticamente la única póliza disponible
    } else if (credits.length > 1) {
      // Selecciona la póliza mas reciente
      let newestIndex = 0;
      let newestDate = new Date(credits[0].inicio_vigencia);

      for (let i = 1; i < credits.length; i++) {
        const currentDate = new Date(credits[i].inicio_vigencia);
        if (currentDate > newestDate) {
          newestDate = currentDate;
          newestIndex = i;
        }
      }

      setSelectedPoliza(newestIndex);
    }
  };

  // Permite almacenar valor props previo al haber una modificación.
  function usePreviousClient(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const prevClient = usePreviousClient(client);

  useEffect(() => {
    // Si se modifica el cliente, se limpia la póliza seleccionada.
    if(prevClient && client && prevClient.id !== client.id) {
      setSelectedPoliza(null);
    }
  }, [client]);

  useEffect(() => {
    const url = `${BASE_URL}${API.simpleCreditos}/`;
    auth.fetch(client ? `${url}?client_id=${client.id}` : url).then(initialLoad);
  }, [client]);
  
  useEffect(() => {
    if (firstLoading && !tableLoading) {
      setFirstLoading(false);
    }
  }, [tableLoading]);

useEffect(() => {
  setTableLoading(true);
  setSelectedVigencia(null);
  setEndosoCredito([]);
  setTiempoPromedioRespuesta({});

  const selected = selectedPoliza;
  if (selected !== null) {
    setLoadingGeneralInfo(true);

    const abortController = new AbortController();
    const signal = abortController.signal;

    const endososCreditoUrlBorrador = `${BASE_URL}${API.simpleEndososCredito}/?poliza_id=${polizas[selected].id}&get_last_including_borrador=true`;
    const endososCreditoUrl = `${BASE_URL}${API.simpleEndososCredito}/?poliza_id=${polizas[selected].id}`;
    const informacionGeneralUrl = `${BASE_URL}${API.lineasCredito}/informacion-general/?poliza_id=${polizas[selected].id}`;
    const tiempoPromedioRespuestaUrl = `${BASE_URL}${API.lineasCredito}/tiempo-promedio-respuesta/?poliza_id=${polizas[selected].id}`;

    const safeAuth = async (url, options = {}) => {
      try {
        return await auth.fetch(url, options);
      } catch (error) {
        if (error.name === 'AbortError' || signal.aborted) {
          return null;
        }
        throw error;
      }
    };

    const endososCreditoBorradorPromise = safeAuth(client ? `${endososCreditoUrlBorrador}&client_id=${client.id}` : endososCreditoUrlBorrador, { signal });
    const endososCreditoPromise = safeAuth(client ? `${endososCreditoUrl}&client_id=${client.id}` : endososCreditoUrl, { signal });
    const informacionGeneralPromise = safeAuth(informacionGeneralUrl, { signal });
    const tiempoPromedioRespuestaPromise = safeAuth(tiempoPromedioRespuestaUrl, {signal})

    Promise.all([endososCreditoBorradorPromise, endososCreditoPromise])
    .then(([endososBorradorResponse, endososRegularResponse]) => {
      if (signal.aborted || selected !== selectedPoliza) {
        return;
      }
    
      setSelectedVigencia(allVigenciaOptions[0]);
    
      if (endososBorradorResponse && endososBorradorResponse.length > 0) {
        const sortedByIdDesc = [...endososBorradorResponse].sort((a, b) => b.id - a.id);
        setEndosoCredito(sortedByIdDesc[0]);
      } else if (endososRegularResponse && endososRegularResponse.length > 0) {
        const sortedByIdDesc = [...endososRegularResponse].sort((a, b) => b.id - a.id);
        setEndosoCredito(sortedByIdDesc[0]);
      } else {
        setEndosoCredito([]);
      }
    })
    .catch((error) => {
      if (!signal.aborted) {
        console.error('Error al obtener los endosos:', error);
      }
    });

    informacionGeneralPromise
    .then((informacionGeneralResponse) => {
      if (signal.aborted || selected !== selectedPoliza) {
        return;
      }      
      if (informacionGeneralResponse) {
        setGeneralInfo(informacionGeneralResponse);
        setLoadingGeneralInfo(false);
      }
    })
    .catch((error) => {
      if (!signal.aborted) {
        setLoadingGeneralInfo(false);
        console.error('Error al obtener la informacion general:', error);
      }
    });

    tiempoPromedioRespuestaPromise
    .then((tiempoPromedioRespuestaResponse) => {
      if (signal.aborted || selected !== selectedPoliza) {
        return;
      }

      if (!('tiempo_promedio' in tiempoPromedioRespuestaResponse)) {
        return;
      }

      const tiempoPromedio = tiempoPromedioRespuestaResponse['tiempo_promedio'];

      const hours = Math.floor(tiempoPromedio / 60);
      const minutes = tiempoPromedio % 60;

      setTiempoPromedioRespuesta({ hours, minutes });
    })
    .catch((error) => {
      if (!signal.aborted) {
        console.error('Error al obtener el tiempo promedio de respuesta:', error);
      }
    });

    return () => {
      abortController.abort();
    };
  } else {
    setEndosoCredito([]);
  }
}, [selectedPoliza]);

  useEffect(() => {
    auth.fetch(`${BASE_URL}${API.estadosLinea}/`).then((response) => {
      setEstadoLineaOptions(
        response.map((elem) =>
          elem.nombre === "Limitada"
            ? { ...elem, nombre: "Aprobado Parcial" }
            : elem
        )
      );
    })
  }, []);

  const polizaOptions = polizas 
    ? polizas.map((poliza, index) => ({
        label: `${poliza.nemotecnico} - ${poliza.ramo_fecu_descripcion}`,
        value: poliza,
        index,
      }))
    : [];
  const xlsxUrl = (selectedVigencia && polizaOptions[selectedPoliza])
    ? `${BASE_URL}${API.lineasCredito}/por-vigencia/xlsx/?poliza_id=${polizaOptions[selectedPoliza].value.id}${
      selectedVigencia.value.id !== -1 ? `&vigencia_id=${selectedVigencia.value.id}` : ''
    }`
    : '';
  const xlsxAllLinesUrl = `${BASE_URL}download-todas-las-lineas`;

  const isPolizaVigente = () => {
    if (selectedVigencia && polizaOptions[selectedPoliza]) {
      const hoy = moment()
      const inicio = moment(polizaOptions[selectedPoliza].value.inicio_vigencia)
      const termino = moment(polizaOptions[selectedPoliza].value.termino_vigencia)
      return hoy.isBetween(inicio, termino, undefined, '[]');
    }
    return false;
  };

  const isPolizaEmitida = () => {
    return polizaOptions[selectedPoliza] && 
           !polizaOptions[selectedPoliza].value.borrador;
  };

  const isPolizaVigenciaFutura = () => {
    if (selectedVigencia && polizaOptions[selectedPoliza]) {
      const hoy = moment()
      const inicio = moment(polizaOptions[selectedPoliza].value.inicio_vigencia)
      return inicio.isAfter(hoy);
    }
    return false;
  };

  useEffect(() => {
    console.log('useEffect triggered', location.state?.fromCheckModal);
    if (location.state && location.state.fromCheckModal) {
      console.log('Starting processing check...');
      const isExportacion = location.state.isExportacion || false;
  
      if (isExportacion) {
        setModalMessage(
          <>
            Solicitud procesada.
            <br />
            Derivado a analista de riesgo de manera automática.
          </>,
        );
        setProcessingModalOpen(true);

        if (location.state.deudorId) {
          const timestamp = new Date().getTime();
          const url = `${BASE_URL}${API.lineasCredito}?client_id=${client.id}&isJudging=${true}&_=${timestamp}&deudorId=${location.state.deudorId}`;
          auth.fetch(url).then((result) => {
            if (result.length > 0) {
              setResultId(result[0].id);
              resultIdRef.current = result[0].id;
            }
          });
        }

        const redirectTimeout = setTimeout(() => {
          setProcessingModalOpen(false);
          handleModalClose();
        }, 5000);
  
        return () => {
          clearTimeout(redirectTimeout);
        };
      }

      setModalMessage(modalMessage);
      setProcessingModalOpen(true);
      if (!location.state.deudorId) {
        console.log('Bulk operation detected - showing loading message');
        setModalMessage('Estamos procesando su solicitud. Recibirá un correo por cada resolución.');
        setProcessingModalOpen(true);
        
        let reloadTimeout;
        const buttonTimeout = setTimeout(() => {
          setShowButton(true);
          reloadTimeout = setTimeout(() => {
            setProcessingModalOpen(false);
            handleModalClose();
            window.location.reload();
          }, 5000);
          
        }, 10000);
  
        return () => {
          clearTimeout(buttonTimeout);
          clearTimeout(reloadTimeout);
        };
      }

      let shouldContinue = true;
  
      const fetchStatus = () => {
        if (!shouldContinue) {
          console.log('Fetch stopped - shouldContinue is false');
          return;
        }
  
        const timestamp = new Date().getTime();
        const url = `${BASE_URL}${API.lineasCredito}?client_id=${client.id}&isJudging=${true}&_=${timestamp}&deudorId=${location.state.deudorId}`;
        console.log('Fetching URL:', url);

        auth
          .fetch(url)
          .then((result) => {
            console.log('API Response:', result);
            
            if (!shouldContinue) {
              console.log('Processing stopped after fetch - shouldContinue is false');
              return;
            }
  
            if (result.length > 0 && !resultIdRef.current) {
              console.log('Initial result processing');
              let targetResult = result.find((item) => 
                item.estado_data.nemotecnico.toLowerCase() === 'pendiente' ||
                (item.balance && item.balance.estado_data.nemotecnico.toLowerCase() === 'procesando')
              );

              if (!targetResult && result.length > 0) {
                targetResult = result[0];
                console.log('No pending item found, using first result with final status');
              }

              if (targetResult) {
                const id = targetResult.id;
                console.log('Found pending/processing item with ID:', id);
                setResultId(id);
                resultIdRef.current = id;
              }
            }
            
            if (resultIdRef.current) {
              console.log('Filtering results by ID:', resultIdRef.current);
              result = result.filter((item) => item.id === resultIdRef.current);
              setResultId(resultIdRef.current);
            }
  
            // Check both the main status and balance status
            const isPending = result.some(item => 
              item.estado_data.nemotecnico.toLowerCase() === 'pendiente' ||
              (item.balance && item.balance.estado_data.nemotecnico.toLowerCase() === 'procesando')
            );
            
            const isErrorCase = result.some(
              (item) =>
                item.balance && item.balance.motivo &&
                (item.balance.motivo.includes("Error al intentar obtener") || 
                 item.balance.motivo.toLowerCase().includes("error") && 
                 item.balance.motivo.toLowerCase().includes("buro"))
            );

            const isMontoBypassAnalista = result.some(
              (item) =>
                item.balance && item.balance.motivo &&
                item.balance.decision_bypass_monto === "Analisis"
            );
            const isMontoBypassExcedido = result.some(
              (item) =>
                item.balance && item.balance.motivo &&
                item.balance.motivo === "PARÁMETRO EXCEDIDO PARA RESOLUCIÓN AUTOMÁTICA, EN REVISIÓN DE ANALISTA."
            );
            const isMontoBypassRechazado = result.some(
              (item) =>
                item.balance && item.balance.motivo &&
                item.balance.decision_bypass_monto === "Rechazado"
            );
            const isAppeal = result.some((item) => {
              if (!item.balance) return false;
              const tipoNemotecnico = item.balance.tipo?.nemotecnico;
              const estadoNemotecnico = item.balance.estado_data?.nemotecnico;
  
              return (
                tipoNemotecnico === 'apelacion' ||
                (tipoNemotecnico === 'reestudio' && estadoNemotecnico !== 'procesando') || 
                tipoNemotecnico === 'desbloqueo'
              );
            });
  
            console.log('Status checks:', {
              isPending,
              isMontoBypassAnalista,
              isMontoBypassExcedido,
              isMontoBypassRechazado,
              isAppeal
            });
  
            // First check if we should stop processing
            if (!isPending) {
              console.log('Case: Final status evaluation');

              if (!resultIdRef.current && result.length > 0) {
                const fallbackId = result[0].id;
                console.log('Setting resultId from fallback', fallbackId);
                setResultId(fallbackId);
                resultIdRef.current = fallbackId;
              }

              const estado = result[0]?.balance?.estado_data?.nemotecnico?.toLowerCase() || 
                           result[0]?.estado_data?.nemotecnico?.toLowerCase();
              console.log('Final estado:', estado);
              setShowButton(true);
              shouldContinue = false;
              if (intervalId) {
                clearInterval(intervalId);
                setIntervalId(null);
              }
  
              history.replace({ ...location, state: { ...location.state, fromCheckModal: false } });
  
              setTimeout(() => {
                if (estado === 'aprobada' || estado === 'aprobado') {
                  console.log('Status: Approved');
                  setModalMessage('Su solicitud de línea fue aceptada');
                } else if (estado === 'rechazada' || estado === 'rechazado') {
                  console.log('Status: Rejected');
                  setModalMessage('Su solicitud de línea fue rechazada');
                } else if (estado === 'limitada') {
                  console.log('Status: Limited');
                  setModalMessage('Su solicitud de línea fue rechazada');
                }
                else if (estado === 'aprobado-parcial') {
                    console.log('Status: Aprobado parcial');
                    setModalMessage('Su solicitud de línea fue aprobada parcialmente');
                } else {
                  console.log('Status: Unknown');
                  setModalMessage('Estado desconocido. Consulte con el administrador.');
                }
  
                
                setProcessingModalOpen(true);
  
                setTimeout(() => {
                  setProcessingModalOpen(false);
                  handleModalClose();
                }, 2000);
              }, 2000);
            }
            // Then check special cases
            else if (isMontoBypassRechazado && !isPending) {
              console.log('Case: MontoBypassRechazado');
              setModalMessage('Su solicitud de línea fue rechazada');
              setProcessingModalOpen(true);
              setShowButton(true);
              setTimeout(() => {
                setProcessingModalOpen(false);
                handleModalClose();
              }, 2000);
  
              history.replace({ ...location, state: { ...location.state, fromCheckModal: false } });
              setStatusPending(false);
  
              shouldContinue = false;
              if (intervalId) {
                clearInterval(intervalId);
                setIntervalId(null);
              }
              setShowButton(true);
            }
            else if ((isPending && isAppeal) || isMontoBypassExcedido || isMontoBypassAnalista || isErrorCase) {
              console.log('Case: Pending with Appeal or MontoBypass cases');

              const requestedAmount = result[0]?.balance?.monto_solicitado;
              let analystMessage = 'Enviando al analista';

              if (requestedAmount && requestedAmount < 1500) {
                  analystMessage = (
                      <>
                        Rechazado por motor automático,<br />
                        pero se deriva a analista de riesgo de manera automática
                      </>
                    );
              }

              setModalMessage(analystMessage);
              setProcessingModalOpen(true);
  
              setTimeout(() => {
                setProcessingModalOpen(false);
                handleModalClose();
              }, 5000);
  
              history.replace({ ...location, state: { ...location.state, fromCheckModal: false } });
              setStatusPending(false);
  
              shouldContinue = false;
              if (intervalId) {
                clearInterval(intervalId);
                setIntervalId(null);
              }
            }
            // If still processing, just continue
            else {
              console.log('Case: Still processing - continue polling');
            }
          })
          .catch((error) => {
            console.error("Error en el fetch:", error);
            setModalMessage("Hubo un problema con la solicitud. Intente más tarde.");
            setProcessingModalOpen(false);
            setShowButton(true);
          });
      };
  
      fetchStatus();
  
      const id = setInterval(() => {
        console.log('Interval tick - shouldContinue:', shouldContinue);
        if (shouldContinue) {
          fetchStatus();
        }
      }, 5000);
      setIntervalId(id);
  
      const buttonTimeout = setTimeout(() => {
        console.log('Button timeout reached');
        setShowButton(true);
        setModalMessage(
          'La solicitud sigue en proceso, puede cerrar esta ventana. Llegará un correo con los resultados.'
        );
      }, 10000);
  
      return () => {
        console.log('Cleanup effect');
        shouldContinue = false;
        if (id) clearInterval(id);
        clearTimeout(buttonTimeout);
      };
    }
  }, [location.state?.fromCheckModal]);
  
  const handleModalClose = () => {
    history.replace({ ...location, state: { ...location.state, fromCheckModal: false } });
  
    setTimeout(() => {
      const currentResultId = resultIdRef.current;
      if (currentResultId) {
        history.push(`/asegurado/lineas-credito/detalle/${currentResultId}`);
      } else {
        console.warn("No se encontró un resultId, no se redirige.");
      }
    }, 1000);
  };

  // Controla las cartas del dashboard en caso que se cambie el filtro de estado en la tabla
  const handleEstadoFilterChange = (newValue) => {
    setEstadoFilter(newValue);
  };

  const handleDashboardCardClick = (newValue) => {
    if (estadoFilter === newValue) {
      setEstadoFilter(-1);
    } else {
      setEstadoFilter(newValue);
    }
  };

  return (
    <>
      {processingModalOpen && (
        <ModalLoadingMotor
          isOpen={processingModalOpen}
          toggle={() => {
            setProcessingModalOpen(false);
            handleModalClose();
          }}
          modalMessage={modalMessage}
          showButton={showButton}
          statusPending={statusPending}
          onClose={handleModalClose}
        />
        )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '450px auto',
          gridGap: '16px',
          alignItems: 'center'
        }}
      >
        <div
          className='z-index-3'
          style={{
            display: 'grid',
            gridTemplateColumns: '65px auto',
            gridGap: '15px 15px',
            padding: '0 0 16px 16px',
            alignItems: 'center',
          }}
        >
          <div className="orsan-label">Póliza:</div>
          {polizaOptions.length > 1 ? (
            <Select
              id="poliza"
              name="poliza"
              className="orsan-form-element"
              value={polizaOptions && polizaOptions[selectedPoliza] ? polizaOptions[selectedPoliza] : null}
              onChange={(selectedOption) => {
                setSelectedPoliza(selectedOption.index);
                setFirstLoading(true);
              }}
              options={polizaOptions}
            />
          ) : (
            <div>{polizaOptions.length === 1 ? polizaOptions[0].label : 'No hay pólizas disponibles'}</div>
          )}
        </div>
        <Row
          style={{
            marginTop: '-64px',
            gap: '12px',
          }}
          className="align-items-stretch"
          noGutters
        >
          <Col className="d-flex flex-column">
            <InfoValueCard
              title="Aprobadas"
              loading={loadingGeneralInfo}
              amount={generalInfo?.valor_total_aprobadas}
              value={generalInfo?.lineas_aprobadas}
              percentage={generalInfo?.lineas_aprobadas_porcentaje}
              moneda={generalInfo?.moneda_asegurada}
              color="var(--orsan-green)"
              className="h-100"
              onClick={() => handleDashboardCardClick(2)}
              selected={estadoFilter === 2}
            />
          </Col>
          <Col className="d-flex flex-column">
            <InfoValueCard
              title="Aprobadas Parcial"
              loading={loadingGeneralInfo}
              amount={generalInfo?.valor_total_aprobadas_parcial}
              value={generalInfo?.lineas_aprobadas_parcial}
              percentage={generalInfo?.lineas_aprobadas_parcial_porcentaje}
              moneda={generalInfo?.moneda_asegurada}
              color="var(--orsan-blue-s3)"
              className="h-100"
              onClick={() => handleDashboardCardClick(3)}
              selected={estadoFilter === 3}
            />
          </Col>
          <Col className="d-flex flex-column">
            <InfoValueCard
              title="Rechazadas"
              loading={loadingGeneralInfo}
              amount={generalInfo?.valor_total_rechazadas}
              value={generalInfo?.lineas_rechazadas}
              percentage={generalInfo?.lineas_rechazadas_porcentaje}
              moneda={generalInfo?.moneda_asegurada}
              color="var(--orsan-red)"
              className="h-100"
              onClick={() => handleDashboardCardClick(4)}
              selected={estadoFilter === 4}
            />
          </Col>
          <Col className="d-flex flex-column">
            <InfoValueCard
              title="Reducidas"
              loading={loadingGeneralInfo}
              amount={generalInfo?.valor_total_reducidas}
              value={generalInfo?.lineas_reducidas}
              percentage={generalInfo?.lineas_reducidas_porcentaje}
              moneda={generalInfo?.moneda_asegurada}
              color="var(--orsan-grey-alt)"
              className="h-100"
              onClick={() => handleDashboardCardClick(5)}
              selected={estadoFilter === 5}
            />
          </Col>
          <Col className="d-flex flex-column">
            <InfoValueCard
              title="Bloqueadas"
              loading={loadingGeneralInfo}
              amount={generalInfo?.valor_total_bloqueadas}
              value={generalInfo?.lineas_bloqueadas}
              percentage={generalInfo?.lineas_bloqueadas_porcentaje}
              moneda={generalInfo?.moneda_asegurada}
              color="var(--orsan-dark-grey)"
              className="h-100"
              onClick={() => handleDashboardCardClick(8)}
              selected={estadoFilter === 8}
            />
          </Col>
          <Col className="d-flex flex-column">
            <InfoValueCard
              title="Deshabilitadas"
              loading={loadingGeneralInfo}
              amount={generalInfo?.valor_total_deshabilitadas}
              value={generalInfo?.lineas_deshabilitadas}
              percentage={generalInfo?.lineas_deshabilitadas_porcentaje}
              moneda={generalInfo?.moneda_asegurada}
              color="var(--custom-light-blue)"
              className="h-100"
              onClick={() => handleDashboardCardClick(7)}
              selected={estadoFilter === 7}
            />
          </Col>
          <Col className="d-flex flex-column">
            <InfoValueCard
              title="Pendientes"
              loading={loadingGeneralInfo}
              amount={generalInfo?.valor_total_pendientes}
              value={generalInfo?.lineas_pendientes}
              percentage={generalInfo?.lineas_pendientes_porcentaje}
              moneda={generalInfo?.moneda_asegurada}
              color="var(--custom-light-blue-s1)"
              className="h-100"
              onClick={() => handleDashboardCardClick(1)}
              selected={estadoFilter === 1}
            />
          </Col>
        </Row>
      </div>
      {selectedVigencia && polizaOptions[selectedPoliza] && (
        <>
          {!isPolizaVigente() && 
              polizaOptions[selectedPoliza].value.borrador && 
              moment(lastEndosoCredito.termino_vigencia).isAfter(moment()) &&
              lastEndosoCredito.estado == 'borrador' && (
            <FormFeedback style={{ paddingLeft: '15px' }}>
              * <strong>Importante:</strong> Esta póliza aún se encuentra en estado de borrador y no ha sido renovada. En caso de que la renovación no se concrete,
              las condiciones y coberturas indicadas en este documento no tendrán validez.
            </FormFeedback>
          )}

          {!isPolizaVigente() && 
           isPolizaEmitida() && 
           isPolizaVigenciaFutura() && (
            <FormFeedback style={{ paddingLeft: '15px' }}>
              * <strong>Importante:</strong> Esta póliza se encuentra emitida pero su vigencia ({moment(polizaOptions[selectedPoliza].value.inicio_vigencia).format('DD/MM/YYYY')}) aún no ha comenzado.
            </FormFeedback>
          )}

          {!isPolizaVigente() && (
            !polizaOptions[selectedPoliza].value.borrador || 
            (polizaOptions[selectedPoliza].value.borrador && lastEndosoCredito.estado != 'borrador')
          ) && !isPolizaVigenciaFutura() && (
            <FormFeedback style={{ paddingLeft: '15px' }}>
              * <strong>Importante:</strong> Esta póliza no se encuentra vigente y no tiene un borrador disponible. No podrá realizar solicitudes de líneas.
            </FormFeedback>
          )}
        </>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content auto max-content max-content max-content',
          gridGap: '10px',
          marginTop: '15px',
          marginBottom: '6px',
        }}
      >
        {Object.keys(tiempoPromedioRespuesta).length > 0 ? (
          <div
            className="pl-2 d-flex align-items-baseline"
            style={{
              borderLeft: '4px solid var(--orsan-blue)',
              borderRadius: '6px',
            }}
          >
            <p
              className="mb-0"
              style={{
                fontSize: '1.1em',
                fontWeight: 600,
                color: 'var(--orsan-dark-grey)'
              }}
            >
              Tiempo promedio de respuesta:
            </p>
            {(tiempoPromedioRespuesta.hours === 0 && tiempoPromedioRespuesta.minutes === 0) ? (
            <p
              className="mb-0 mt-1 ml-3"
              style={{
                fontSize: '1.1em',
                fontWeight: 600,
                color: 'var(--orsan-dark-grey)'
              }}
            >
              Menos de un minuto
            </p>
            ) : (
              <>
                <div className="ml-3">
                  <span
                    className="mr-2"
                    style={{
                      fontSize: '1.25em',
                      fontWeight: 600,
                      color: 'var(--orsan-blue-s3)'
                    }}
                  >
                    {roundInt(tiempoPromedioRespuesta.hours)}
                  </span>
                  horas
                </div>
                <div className="ml-2">
                  <span
                    className="mr-2"
                    style={{
                      fontSize: '1.15em',
                      fontWeight: 600,
                      color: 'var(--orsan-blue)'
                    }}
                  >
                    {tiempoPromedioRespuesta.minutes}
                  </span>
                  minutos
                </div>
              </>
            )}
            <Icon
              id="avg-response-time-info"
              placement="right"
              tooltipText="El cálculo del tiempo promedio excluye las líneas migradas, ya que provienen del sistema anterior. Solo se consideran las líneas solicitadas en esta plataforma."
              className="ml-2"
            >
              <i 
                className="fas fa-info-circle" 
                style={{
                  color: 'var(--orsan-blue)',
                  cursor: 'pointer'
                }}
              />
            </Icon>
          </div>
        ) : <div />}
        <div />
        <div>
          {selectedPoliza !== null && isUserHasProfile(LINEA_CREDITO_WRITE) && 
            polizaOptions[selectedPoliza] &&
            moment(lastEndosoCredito.termino_vigencia).isAfter(moment()) &&
            lastEndosoCredito.estado !== 'construccion' && (
            <Link
              className="btn-orsan-primary btn btn-secondary"
              to={{
                pathname: `/asegurado/linea-credito/${polizaOptions[selectedPoliza].value.id}`,
                state: { returnTo: "/asegurado/lineas-credito/" },
              }}
              style={{ width: '170px', fontSize: '14px' }}
            >
              Solicitud de Línea
            </Link>
          )}
        </div>
        <div>
          <DropdownDownloadBtn
            id="xlsx-download"
            className="btn btn-orsan-primarydwn"
            text="Mi Cartera Actual"
            tipText="Descargar Últimos Estados. Esto se puede demorar unos minutos."
            url={xlsxUrl}
            dropdownOptions={
              user.is_orsan
                ? [
                    {
                      text: 'Todas las Líneas',
                      url: xlsxAllLinesUrl,
                    },
                  ]
                : []
            }
          />
        </div>
        <div>
          <DropdownDownloadBtn
            id="xlsx-historial-download"
            className="btn btn-orsan-primarydwn"
            text="Mi Cartera Historica"
            tipText="Descargar Historial de Estados. Esto se puede demorar unos minutos."
            url={xlsxUrl ? `${xlsxUrl}&historial` : ''}
            width="12rem"
            dropdownOptions={
              user.is_orsan
                ? [
                    {
                      text: 'Todas las Líneas',
                      url: xlsxAllLinesUrl ? `${xlsxAllLinesUrl}?historial` : '',
                    },
                    {
                      text: 'Líneas (Sin Pendientes)',
                      url: xlsxAllLinesUrl ? `${xlsxAllLinesUrl}?historial-non-pending` : '',
                    },
                  ]
                : []
            }
          />
        </div>
      </div>
      {polizaOptions[selectedPoliza] && (
        <>
          <LoadingSpinner isLoading={firstLoading} overlayHeight='28rem' />
          {selectedVigencia && (
            <CreditPolicyDeudorLineasTable
              client={client}
              vigencia={selectedVigencia.value}
              poliza={polizaOptions[selectedPoliza].value}
              estadoLineaOptions={estadoLineaOptions}
              isPolizaVigente={isPolizaVigente}
              estadoFilter={estadoFilter}
              onEstadoFilterChange={handleEstadoFilterChange}
              loading={tableLoading}
              setLoading={setTableLoading}
              hideLoadingText={firstLoading}
              lastEndosoCredito={lastEndosoCredito}
              isOnBorrador={!!polizaOptions[selectedPoliza].value.borrador}
            />
          )}
        </>
      )}
    </>
  );
};

CreditPolicyDeudorLineas.propTypes = {
  locationSearch: PropTypes.string.isRequired,
  client: PropTypes.shape({
    id: PropTypes.number,
  }),
};

CreditPolicyDeudorLineas.defaultProps = {
  client: null,
};

export default CreditPolicyDeudorLineas;
